import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Select,
  Spin,
  message,
  Button,
  Table,
  Space,
  Form,
  Typography,
  Input,
} from "antd";
import { PlusOutlined, UserOutlined } from "@ant-design/icons";

import { obtenerUsuariosPorSuperior } from "../../../services/tropas";
import { getDirectusClient } from "../../../lib/directus";
import { createItem, readItems, updateItem, updateItems } from "@directus/sdk";
import { fetchUserLoans } from "../../../features/prestamos/prestamoSlice";
import { formatPrice } from "../../../lib/utils";
import { Modal as AntdModal } from "antd";

const { Option } = Select;
const { Title } = Typography;

const CreateSalesmanTroops = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.userdata.id);
  const userBodega = useSelector((state) => state.userdata.bodega?.id);
  const signoMoneda = useSelector((state) => state.signoMoneda.signo_moneda);
  const { userLoans } = useSelector((state) => state.prestamos);

  const [subordinates, setSubordinates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedLoan, setSelectedLoan] = useState(null);

  const [createLoanLoading, setCreateLoanLoading] = useState(false);

  const [form] = Form.useForm();
  const [userActiveLoan, setUserActiveLoan] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [serialsForProduct, setSerialsForProduct] = useState([]);
  const [selectedSerials, setSelectedSerials] = useState([]);
  const [cantidadNoSerial, setCantidadNoSerial] = useState(1);
  const [productosParaPrestar, setProductosParaPrestar] = useState([]);
  const [serialModalVisible, setSerialModalVisible] = useState(false);
  const [tempSelectedSerials, setTempSelectedSerials] = useState([]);
  const [selectedNSItem, setSelectedNSItem] = useState(null);

  const [cantidadAgregadaPorProducto, setCantidadAgregadaPorProducto] =
    useState({});

  const [selectedUserInfo, setSelectedUserInfo] = useState(null);

  const fetchSubordinates = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const response = await obtenerUsuariosPorSuperior(user);
      setSubordinates(response);
    } catch (error) {
      console.error("Error obteniendo subordinados:", error);
      message.error("Error al cargar los vendedores");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubordinates();
    if (user) {
      dispatch(fetchUserLoans(user));
    }
  }, [user, dispatch]);

  // Buscar todos los productos de todos los préstamos activos del usuario (supervisor)
  useEffect(() => {
    if (userLoans && userLoans.length > 0) {
      const activeLoans = userLoans.filter((l) => l.estado === "activo");

      const serialProducts = activeLoans
        .flatMap((loan) =>
          (loan.seriales || [])
            .filter((item) => item.seriales_id && item.seriales_id.producto)
            .map((item) => item.seriales_id.producto)
        )
        .filter(Boolean);

      const nsProducts = activeLoans
        .flatMap((loan) =>
          (loan.inventario_nsp || [])
            .filter(
              (item) =>
                item.inventario_ns_prestamo_id &&
                item.inventario_ns_prestamo_id.inventario_ns
            )
            .map((item) => item.inventario_ns_prestamo_id.inventario_ns)
        )
        .filter(Boolean);

      const allProducts = [...serialProducts, ...nsProducts];
      const uniqueProducts = Array.from(
        new Map(allProducts.map((p) => [p.id, p])).values()
      );
      setProductOptions(uniqueProducts);
      setUserActiveLoan(activeLoans.length > 0 ? activeLoans[0] : null);
    } else {
      setProductOptions([]);
      setUserActiveLoan(null);
    }
  }, [userLoans]);

  const handleProductSelect = (productId) => {
    setSelectedProduct(productId);
    setCantidadNoSerial(1);
    setSelectedNSItem(null);

    const existingProduct = productosParaPrestar.find(
      (p) => p.producto && p.producto.id === productId && p.seriales
    );

    if (existingProduct) {
      setSelectedSerials(existingProduct.seriales.map((s) => s.id));
      setTempSelectedSerials(existingProduct.seriales.map((s) => s.id));
    } else {
      setSelectedSerials([]);
      setTempSelectedSerials([]);
    }
    if (!userLoans) return;

    const serials = userLoans
      .filter((l) => l.estado === "activo")
      .flatMap((loan) => loan.seriales || [])
      .filter(
        (item) =>
          item.seriales_id &&
          item.seriales_id.producto &&
          item.seriales_id.producto.id === productId &&
          item.seriales_id.estado === 3
      )
      .map((item) => ({
        id: item.seriales_id.id,
        serial: item.seriales_id.serial,
        estado: item.seriales_id.estado,
        producto: item.seriales_id.producto,
      }));
    setSerialsForProduct(serials);

    const nsItem = userLoans
      .filter((l) => l.estado === "activo")
      .flatMap((loan) => loan.inventario_nsp || [])
      .find(
        (item) =>
          item.inventario_ns_prestamo_id &&
          item.inventario_ns_prestamo_id.inventario_ns &&
          item.inventario_ns_prestamo_id.inventario_ns.id === productId
      );
    setSelectedNSItem(nsItem || null);
  };

  const handleAddProduct = () => {
    if (!selectedProduct) return;
    const producto = productOptions.find((p) => p.id === selectedProduct);

    if (!producto) {
      message.error("Producto no encontrado");
      return;
    }

    if (serialsForProduct.length > 0) {
      if (selectedSerials.length === 0) {
        message.error("Selecciona al menos un serial");
        return;
      }

      const existingProductIndex = productosParaPrestar.findIndex(
        (p) => p.producto && p.producto.id === selectedProduct && p.seriales
      );

      if (existingProductIndex !== -1) {
        const existingProduct = productosParaPrestar[existingProductIndex];
        const newSerials = serialsForProduct.filter((s) =>
          selectedSerials.includes(s.id)
        );

        const existingSerialIds = new Set(
          existingProduct.seriales.map((s) => s.id)
        );

        const duplicateSerials = newSerials.filter((s) =>
          existingSerialIds.has(s.id)
        );

        if (duplicateSerials.length > 0) {
          message.error("Algunos seriales ya están seleccionados");
          return;
        }

        const updatedProductos = [...productosParaPrestar];
        updatedProductos[existingProductIndex] = {
          ...existingProduct,
          seriales: [...existingProduct.seriales, ...newSerials],
          cantidad: existingProduct.seriales.length + newSerials.length,
        };
        setProductosParaPrestar(updatedProductos);
      } else {
        setProductosParaPrestar([
          ...productosParaPrestar,
          {
            producto,
            seriales: serialsForProduct.filter((s) =>
              selectedSerials.includes(s.id)
            ),
            cantidad: selectedSerials.length,
          },
        ]);
      }

      setCantidadAgregadaPorProducto((prev) => ({
        ...prev,
        [selectedProduct]:
          (prev[selectedProduct] || 0) + selectedSerials.length,
      }));
    } else {
      const maxCantidad =
        selectedNSItem?.inventario_ns_prestamo_id?.cantidad || 0;

      const cantidadYaAgregada =
        cantidadAgregadaPorProducto[selectedProduct] || 0;
      const cantidadDisponibleRestante = maxCantidad - cantidadYaAgregada;

      if (
        cantidadNoSerial < 1 ||
        cantidadNoSerial > cantidadDisponibleRestante
      ) {
        message.error(
          `Cantidad debe ser entre 1 y ${cantidadDisponibleRestante}`
        );
        return;
      }

      // Buscar si ya existe este producto en la lista
      const existingProductIndex = productosParaPrestar.findIndex(
        (p) => p.producto && p.producto.id === selectedProduct && p.cantidad
      );

      if (existingProductIndex !== -1) {
        // Si ya existe, sumar la nueva cantidad
        const existingProduct = productosParaPrestar[existingProductIndex];
        const updatedProductos = [...productosParaPrestar];
        updatedProductos[existingProductIndex] = {
          ...existingProduct,
          cantidad: existingProduct.cantidad + cantidadNoSerial,
        };
        setProductosParaPrestar(updatedProductos);
      } else {
        // Si no existe, agregar nuevo producto
        setProductosParaPrestar([
          ...productosParaPrestar,
          {
            producto,
            cantidad: cantidadNoSerial,
          },
        ]);
      }

      // Actualizar cantidad agregada para no serializados
      setCantidadAgregadaPorProducto((prev) => ({
        ...prev,
        [selectedProduct]: (prev[selectedProduct] || 0) + cantidadNoSerial,
      }));
    }
    setSelectedProduct(null);
    setSelectedSerials([]);
    setCantidadNoSerial(1);
  };

  const handleUserSelect = async (value) => {
    setSelectedUser(value);
    setSelectedLoan(null);

    setSelectedSerials([]);
    setCantidadAgregadaPorProducto({});
    setProductosParaPrestar([]);
    setSelectedProduct(null);
    setSerialsForProduct([]);
    setCantidadNoSerial(1);
    setSelectedNSItem(null);

    // Buscar info del usuario seleccionado (tope, usado, disponible)
    // Buscar en subordinates
    const info = subordinates.find((s) => s.id === value);
    if (info) {
      setSelectedUserInfo({
        tope_de_prestamo: info.tope_de_prestamo,
        prestamo_usado: info.prestamo_usado,
        nombre: `${info.first_name} ${info.last_name}`,
      });
    } else {
      setSelectedUserInfo(null);
    }
  };

  const checkSerialsAvailability = async (serialIds) => {
    try {
      const directus = await getDirectusClient();

      // Verificar si los seriales ya están en préstamos_tropas
      const response = await directus.request(
        readItems("prestamos_tropas", {
          fields: ["id", "seriales.seriales_id.id"],
          filter: {
            seriales: {
              seriales_id: {
                id: { _in: serialIds },
              },
            },
          },
        })
      );

      // Crear un set de seriales ya prestados
      const alreadyLoanedSerials = new Set();
      response.forEach((loan) => {
        loan.seriales?.forEach((serial) => {
          alreadyLoanedSerials.add(serial.seriales_id.id);
        });
      });

      return alreadyLoanedSerials;
    } catch (error) {
      console.error("Error checking serials availability:", error);
      return new Set();
    }
  };

  // REWRITE: Fix error when creating troop loan if selectedLoan is null
  const createTroopLoan = async (values) => {
    if (!selectedUser || productosParaPrestar.length === 0) {
      message.error(
        "Debe seleccionar un vendedor y al menos un producto/serial"
      );
      return;
    }

    // Verificar que los seriales seleccionados no estén ya prestados
    const serialIds = productosParaPrestar.flatMap((item) => {
      if (item.seriales) {
        return item.seriales.map((s) => s.id);
      }
      return [];
    });
    const alreadyLoanedSerials = await checkSerialsAvailability(serialIds);

    const conflictingSerials = productosParaPrestar.flatMap((item) => {
      if (item.seriales) {
        return item.seriales.filter((s) => alreadyLoanedSerials.has(s.id));
      }
      return [];
    });
    if (conflictingSerials.length > 0) {
      const serialList = conflictingSerials.map((s) => s.serial).join(", ");
      message.error(
        `Los siguientes seriales ya están prestados: ${serialList}`
      );
      return;
    }

    // Validar que la suma de cantidades no serializadas no exceda el disponible
    for (const item of productosParaPrestar.filter((p) => !p.seriales)) {
      // Buscar el registro original en todos los préstamos activos del usuario
      const nsItemOriginal = userLoans
        .filter((l) => l.estado === "activo")
        .flatMap((loan) => loan.inventario_nsp || [])
        .find(
          (ns) =>
            ns.inventario_ns_prestamo_id.inventario_ns?.id === item.producto.id
        );
      const cantidadDisponible =
        nsItemOriginal?.inventario_ns_prestamo_id?.cantidad || 0;
      // Sumar todas las cantidades de este producto en productosParaPrestar
      const cantidadSolicitada = productosParaPrestar
        .filter((p) => !p.seriales && p.producto.id === item.producto.id)
        .reduce((acc, curr) => acc + (curr.cantidad || 0), 0);
      if (cantidadSolicitada > cantidadDisponible) {
        message.error(
          `No puedes prestar más de la cantidad disponible (${cantidadDisponible}) para el producto ${item.producto.nombre}`
        );
        return;
      }
    }

    setCreateLoanLoading(true);
    try {
      const directus = await getDirectusClient();

      // Determinar el préstamo de origen (prestamo_origen)
      // Si userActiveLoan existe, usar su id, si no, dejar null
      let prestamoOrigenId = null;
      if (selectedLoan && selectedLoan.id) {
        prestamoOrigenId = selectedLoan.id;
      } else if (userActiveLoan && userActiveLoan.id) {
        prestamoOrigenId = userActiveLoan.id;
      } else {
        // Si no hay préstamo de origen, mostrar error y salir
        message.error(
          "No se encontró un préstamo de origen activo para este usuario."
        );
        setCreateLoanLoading(false);
        return;
      }

      // Crear préstamo de tropas
      const troopLoanData = {
        vendedor: selectedUser,
        tipo: "prestamo_tropas",
        estado: "solicitud_prestamo",
        bodega: userBodega,
        prestamo_origen: prestamoOrigenId,
      };

      const troopLoanResponse = await directus.request(
        createItem("prestamos_tropas", troopLoanData)
      );

      if (troopLoanResponse.id) {
        // Serializados: igual que antes
        await directus.request(
          updateItem("prestamos_tropas", troopLoanResponse.id, {
            seriales: {
              create: productosParaPrestar
                .filter((item) => item.seriales)
                .flatMap((item) =>
                  item.seriales.map((serial) => ({
                    prestamos_tropas_id: "+",
                    seriales_id: { id: serial.id },
                  }))
                ),
              update: [],
              delete: [],
            },
          })
        );

        // NO SERIALIZADOS: crear inventario_ns_prestamo y restar cantidad al supervisor
        for (const item of productosParaPrestar.filter((p) => !p.seriales)) {
          // 1. Crear inventario_ns_prestamo para el préstamo de tropas
          await directus.request(
            createItem("inventario_ns_prestamo", {
              cantidad: item.cantidad,
              estado: "solicitud_prestamo",
              inventario_ns: item.producto.id,
              prestamos_tropas: {
                create: [
                  {
                    inventario_ns_prestamo_id: "+",
                    prestamos_tropas_id: { id: troopLoanResponse.id },
                  },
                ],
              },
            })
          );

          // 2. Restar cantidad al registro original del supervisor
          // Buscar el registro original en todos los préstamos activos del usuario
          const nsItemOriginal = userLoans
            .filter((l) => l.estado === "activo")
            .flatMap((loan) => loan.inventario_nsp || [])
            .find(
              (ns) =>
                ns.inventario_ns_prestamo_id.inventario_ns?.id ===
                item.producto.id
            );
          if (nsItemOriginal) {
            const nuevaCantidad = Math.max(
              0,
              parseInt(nsItemOriginal.inventario_ns_prestamo_id.cantidad) -
                item.cantidad
            );
            await directus.request(
              updateItem(
                "inventario_ns_prestamo",
                nsItemOriginal.inventario_ns_prestamo_id.id,
                {
                  cantidad: nuevaCantidad,
                }
              )
            );
          }
        }

        // Actualizar estado de seriales
        const serialIds = productosParaPrestar.flatMap((item) => {
          if (item.seriales) {
            return item.seriales.map((serial) => serial.id);
          }
          return [];
        });
        if (serialIds.length > 0) {
          await directus.request(
            updateItems("seriales", serialIds, {
              estado: 9,
            })
          );
        }

        message.success(
          `Préstamo de tropas creado exitosamente con ${productosParaPrestar.length} productos/seriales`
        );

        // Limpiar formulario
        setSelectedUser(null);
        setSelectedLoan(null);
        setUserActiveLoan(null);
        setProductOptions([]);
        setSelectedProduct(null);
        setSerialsForProduct([]);
        setSelectedSerials([]);
        setCantidadNoSerial(1);
        setProductosParaPrestar([]);
        setCantidadAgregadaPorProducto({});
        setSelectedUserInfo(null);
        form.resetFields();

        // Recargar préstamos del usuario
        dispatch(fetchUserLoans(user));
        // Notificar al padre para recargar prestamos_tropas
        if (typeof onSuccess === "function") {
          onSuccess();
        }
      }
    } catch (error) {
      console.error("Error creating troop loan:", error);
      message.error("Error al crear el préstamo de tropas");
    } finally {
      setCreateLoanLoading(false);
    }
  };

  const calculateTotal = () => {
    return productosParaPrestar.reduce((total, item) => {
      if (!item.producto) return total;
      const precio = parseFloat(item.producto.preciosinimp) || 0;
      const impuesto = parseFloat(item.producto.grupo?.impuesto) || 0;
      const cantidad = item.cantidad || 1; // Default to 1 if not specified
      return total + (precio + (precio * impuesto) / 100) * cantidad;
    }, 0);
  };

  // UI para seleccionar seriales en un modal
  const serialColumns = [
    {
      title: "Serial",
      dataIndex: "serial",
      key: "serial",
    },
    {
      title: "Producto",
      dataIndex: ["producto", "nombre"],
      key: "producto",
    },
  ];

  // Tabla para mostrar productos/seriales/cantidades a prestar
  const resumenColumns = [
    {
      title: "Producto",
      dataIndex: ["producto", "nombre"],
      key: "producto",
      render: (_, record) =>
        record.producto ? record.producto.nombre || "" : "",
    },
    {
      title: "Tipo",
      key: "tipo",
      render: (_, record) =>
        record.seriales ? "Serializado" : "No serializado",
    },
    {
      title: "Seriales",
      key: "seriales",
      render: (_, record) =>
        record.seriales
          ? record.seriales.map((s) => s.serial || "").join(", ")
          : "-",
    },
    {
      title: "Cantidad",
      dataIndex: "cantidad",
      key: "cantidad",
      render: (cantidad, record) =>
        record.seriales ? record.seriales.length : cantidad,
    },
    {
      title: "Acciones",
      key: "acciones",
      render: (_, record) => (
        <Button
          danger
          size="small"
          onClick={() => {
            setProductosParaPrestar(
              productosParaPrestar.filter((p) => p !== record)
            );
            // Restar la cantidad de este producto del control de cantidad agregada
            setCantidadAgregadaPorProducto((prev) => {
              const nuevo = { ...prev };
              if (record.producto && record.producto.id) {
                if (record.seriales) {
                  nuevo[record.producto.id] =
                    (nuevo[record.producto.id] || 0) - record.seriales.length;
                  if (nuevo[record.producto.id] <= 0)
                    delete nuevo[record.producto.id];
                } else {
                  nuevo[record.producto.id] =
                    (nuevo[record.producto.id] || 0) - (record.cantidad || 0);
                  if (nuevo[record.producto.id] <= 0)
                    delete nuevo[record.producto.id];
                }
              }
              return nuevo;
            });
          }}
        >
          Quitar
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <Title level={3}>Crear Préstamo de Vendedor Tropas</Title>

      <div style={{ marginBottom: "20px" }}>
        <label
          style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}
        >
          Seleccionar vendedor:
        </label>
        <Select
          placeholder="Selecciona un vendedor"
          style={{ width: "100%", maxWidth: "400px" }}
          loading={loading}
          onChange={handleUserSelect}
          value={selectedUser}
          notFoundContent={
            loading ? <Spin size="small" /> : "No hay vendedores disponibles"
          }
        >
          {subordinates.map((subordinate) => (
            <Option key={subordinate.id} value={subordinate.id}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>
                  <UserOutlined /> {subordinate.first_name}{" "}
                  {subordinate.last_name}
                </span>
              </div>
            </Option>
          ))}
        </Select>
      </div>

      {/* Mostrar información del vendedor seleccionado (tope, usado, disponible) */}
      {selectedUserInfo && (
        <div style={{ marginBottom: "20px" }}>
          <Space size={"large"}>
            <span>
              <b>Máximo de préstamo:</b>{" "}
              {formatPrice(selectedUserInfo.tope_de_prestamo, signoMoneda)}
            </span>
            <span style={{ color: "red" }}>
              <b>Préstamo usado:</b>{" "}
              {formatPrice(selectedUserInfo.prestamo_usado, signoMoneda)}
            </span>
            <span style={{ color: "green" }}>
              <b>Disponible:</b>{" "}
              {formatPrice(
                (selectedUserInfo.tope_de_prestamo || 0) -
                  (selectedUserInfo.prestamo_usado || 0),
                signoMoneda
              )}
            </span>
          </Space>
        </div>
      )}

      {selectedUser && (
        <div style={{ marginBottom: "20px" }}>
          <Title level={4}>Productos disponibles en el préstamo</Title>
          <Select
            placeholder="Selecciona un producto"
            style={{ width: "100%", maxWidth: "400px", marginBottom: "10px" }}
            loading={productOptions.length === 0}
            onChange={handleProductSelect}
            value={selectedProduct}
            notFoundContent={
              productOptions.length === 0 ? (
                <Spin size="small" />
              ) : (
                "No hay productos disponibles"
              )
            }
          >
            {productOptions.map((product) => (
              <Option key={product.id} value={product.id}>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span>{product.nombre}</span>
                </div>
              </Option>
            ))}
          </Select>

          {selectedProduct && serialsForProduct.length > 0 && (
            <div style={{ marginBottom: "10px" }}>
              <Button
                type="primary"
                onClick={() => {
                  // Obtener los seriales ya agregados para este producto
                  const existingProduct = productosParaPrestar.find(
                    (p) =>
                      p.producto &&
                      p.producto.id === selectedProduct &&
                      p.seriales
                  );
                  const existingSerialIds = existingProduct
                    ? existingProduct.seriales.map((s) => s.id)
                    : [];
                  setTempSelectedSerials(existingSerialIds);
                  setSerialModalVisible(true);
                }}
              >
                Ver seriales
              </Button>
              <AntdModal
                title="Selecciona seriales para prestar"
                open={serialModalVisible}
                onOk={() => {
                  // Actualizar la lista de productos basándose en los cambios
                  const existingProductIndex = productosParaPrestar.findIndex(
                    (p) =>
                      p.producto &&
                      p.producto.id === selectedProduct &&
                      p.seriales
                  );

                  if (existingProductIndex !== -1) {
                    // Si el producto ya existe, actualizar sus seriales
                    const existingProduct =
                      productosParaPrestar[existingProductIndex];
                    const newSerials = serialsForProduct.filter((s) =>
                      tempSelectedSerials.includes(s.id)
                    );

                    const updatedProductos = [...productosParaPrestar];
                    updatedProductos[existingProductIndex] = {
                      ...existingProduct,
                      seriales: newSerials,
                      cantidad: newSerials.length,
                    };
                    setProductosParaPrestar(updatedProductos);

                    // Actualizar cantidad agregada
                    setCantidadAgregadaPorProducto((prev) => ({
                      ...prev,
                      [selectedProduct]: newSerials.length,
                    }));
                  } else if (tempSelectedSerials.length > 0) {
                    // Si no existe y hay seriales seleccionados, agregar nuevo producto
                    const producto = productOptions.find(
                      (p) => p.id === selectedProduct
                    );
                    const newSerials = serialsForProduct.filter((s) =>
                      tempSelectedSerials.includes(s.id)
                    );

                    setProductosParaPrestar([
                      ...productosParaPrestar,
                      {
                        producto,
                        seriales: newSerials,
                        cantidad: newSerials.length,
                      },
                    ]);

                    // Actualizar cantidad agregada
                    setCantidadAgregadaPorProducto((prev) => ({
                      ...prev,
                      [selectedProduct]: newSerials.length,
                    }));
                  }

                  setSelectedSerials(tempSelectedSerials);
                  setSerialModalVisible(false);
                }}
                onCancel={() => setSerialModalVisible(false)}
                width={700}
              >
                <Table
                  columns={serialColumns}
                  dataSource={serialsForProduct}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  size="small"
                  rowSelection={{
                    type: "checkbox",
                    selectedRowKeys: tempSelectedSerials,
                    onChange: (selectedRowKeys) =>
                      setTempSelectedSerials(selectedRowKeys),
                  }}
                />
              </AntdModal>
            </div>
          )}

          {selectedProduct &&
            serialsForProduct.length === 0 &&
            selectedNSItem && (
              <div style={{ marginBottom: "10px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "bold",
                  }}
                >
                  {/* Mostrar la cantidad disponible restando lo que el usuario ya agregó */}
                  Cantidad disponible (máx:{" "}
                  {(() => {
                    const max =
                      parseInt(
                        selectedNSItem.inventario_ns_prestamo_id.cantidad
                      ) || 0;
                    const yaAgregada =
                      cantidadAgregadaPorProducto[selectedProduct] || 0;
                    return Math.max(0, max - yaAgregada);
                  })()}
                  )
                </label>
                <Input
                  type="number"
                  min="1"
                  max={(() => {
                    const max =
                      parseInt(
                        selectedNSItem.inventario_ns_prestamo_id.cantidad
                      ) || 1;
                    const yaAgregada =
                      cantidadAgregadaPorProducto[selectedProduct] || 0;
                    return Math.max(1, max - yaAgregada);
                  })()}
                  value={cantidadNoSerial}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    setCantidadNoSerial(value);
                  }}
                  style={{ width: "100px" }}
                  placeholder="Cantidad"
                />
              </div>
            )}

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddProduct}
            disabled={
              !selectedProduct ||
              (serialsForProduct.length > 0 && selectedSerials.length === 0) ||
              (serialsForProduct.length === 0 &&
                (!selectedNSItem || cantidadNoSerial < 1))
            }
          >
            Agregar al préstamo
          </Button>
        </div>
      )}

      {/* Tabla de productos/seriales/cantidades a prestar */}
      <div style={{ margin: "20px 0" }}>
        <Title level={4}>
          Productos a prestar ({productosParaPrestar.length})
        </Title>
        <Table
          columns={resumenColumns}
          dataSource={productosParaPrestar}
          rowKey={(record) =>
            record.producto.id + (record.seriales ? "-serial" : "-ns")
          }
          pagination={false}
          size="small"
        />
        <div style={{ marginTop: "10px", fontWeight: "bold" }}>
          Total: {formatPrice(calculateTotal(), signoMoneda)}
        </div>
      </div>

      {selectedUser && productosParaPrestar.length > 0 && (
        <Form form={form} onFinish={createTroopLoan}>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={createLoanLoading}
              icon={<PlusOutlined />}
            >
              Crear Préstamo de Tropas
            </Button>
          </Form.Item>
        </Form>
      )}
    </div>
  );
};

export default CreateSalesmanTroops;
